<!-- File Upload Section -->
<div class="mb-4" data-testid="file-upload-section">
  <div class="flex align-items-center justify-content-between mb-3">
    <h5
      class="text-base font-semibold text-900 m-0"
      data-testid="file-upload-label"
    >
      <i class="fa fa-paperclip mr-2 text-primary"></i>
      {{ 'FREEZE.FREEZE_SIDEBAR.ATTACHMENTS' | translate }}
    </h5>
    @if (isUploading()) {
      <p-progressSpinner
        styleClass="w-1rem h-1rem"
        strokeWidth="4"
        data-testid="file-uploading-spinner"
      />
    }
  </div>

  <!-- Required Documents Info Message -->
  @if (selectedFreezeReason()?.required_doc_pri_lang) {
    <div class="mb-3" data-testid="required-docs-reminder">
      <p-message severity="warn" styleClass="w-full">
        <ng-template pTemplate="content">
          <div class="flex align-items-start gap-2">
            <i class="fa fa-exclamation-circle text-orange-500 mt-1"></i>
            <div class="flex-1">
              <div class="font-semibold text-900 mb-1">
                {{ 'FREEZE.FREEZE_SIDEBAR.REQUIRED_DOCUMENTS_REMINDER' | translate }}
              </div>
              <div class="text-sm text-700">
                {{ selectedFreezeReason()?.required_doc_pri_lang }}
              </div>
            </div>
          </div>
        </ng-template>
      </p-message>
    </div>
  }

  <!-- File Upload Drop Zone - Only show when no files uploaded -->
  @if (uploadedFiles().length === 0) {
    <div
      class="border-round-lg overflow-hidden border-1 border-200 relative"
      data-testid="file-upload-dropzone"
      style="isolation: isolate"
    >
      <div
        class="border-2 border-dashed border-300 border-round-lg p-4 text-center relative hover:border-primary hover:bg-primary-50 transition-all transition-duration-200 cursor-pointer"
        style="min-height: 140px; background: linear-gradient(135deg, var(--surface-50) 0%, var(--surface-0) 100%);"
      >
        <!-- File Drop Zone -->
        <ngx-file-drop
          class="opacity-0 absolute w-full h-full top-0 left-0 cursor-pointer"
          style="z-index: 2"
          [accept]="acceptedFileTypes.join(',')"
          (onFileDrop)="onFileDrop($event)"
          aria-label="file-drop-zone"
          data-testid="file-drop-zone"
        >
          <ng-template ngx-file-drop-content-tmp let-openFileSelector="openFileSelector">
            <div
              (click)="openFileSelector()"
              class="w-full h-full absolute left-0 top-0"
              aria-label="file-drop-zone-click"
              data-testid="file-drop-zone-click"
            ></div>
          </ng-template>
        </ngx-file-drop>

        <!-- Upload Icon and Text -->
        <div
          class="flex flex-column align-items-center justify-content-center gap-2 relative"
          style="z-index: 1; pointer-events: none"
        >
          <div class="bg-primary-100 border-circle w-4rem h-4rem flex align-items-center justify-content-center mb-2">
            <i class="fa fa-cloud-upload text-3xl text-primary"></i>
          </div>
          <p class="text-base font-semibold text-900 m-0 mb-1" data-testid="upload-instruction-text">
            {{ 'FREEZE.FREEZE_SIDEBAR.DRAG_DROP_FILE' | translate }}
          </p>
          <p class="text-sm text-600 m-0 mb-2">
            {{ 'FREEZE.FREEZE_SIDEBAR.OR' | translate }}
            <span class="text-primary font-bold cursor-pointer hover:underline">
              {{ 'FREEZE.FREEZE_SIDEBAR.BROWSE_FILE' | translate }}
            </span>
          </p>
          <div class="surface-100 border-round px-3 py-2">
            <p class="text-xs text-600 m-0" data-testid="file-type-info">
              <i class="fa fa-info-circle mr-1 text-primary"></i>
              <strong>{{ 'FREEZE.FREEZE_SIDEBAR.ACCEPTED_FILE_TYPES' | translate }}:</strong>
              PDF, PNG, JPG, JPEG, GIF, DOC, DOCX
            </p>
            <p class="text-xs text-600 m-0 mt-1">
              <i class="fa fa-database mr-1 text-primary"></i>
              <strong>{{ 'FREEZE.FREEZE_SIDEBAR.MAX_FILE_SIZE' | translate }}:</strong>
              5MB
            </p>
          </div>
        </div>
      </div>
    </div>
  }

  <!-- Uploaded Files List -->
  @if (uploadedFiles().length > 0) {
    <div class="mt-3" data-testid="uploaded-files-list">
      <div class="surface-0 border-round-lg border-1 border-200 overflow-hidden">
        <div class="p-3 border-bottom-1 border-200 bg-primary-50">
          <div class="flex align-items-center justify-content-between">
            <span class="font-semibold text-sm text-900" data-testid="uploaded-files-count">
              <i class="fa fa-check-circle text-green-500 mr-2"></i>
              {{ 'FREEZE.FREEZE_SIDEBAR.UPLOADED_FILES' | translate }} ({{ uploadedFiles().length }})
            </span>
          </div>
        </div>
        <div class="p-2">
          @for (
            uploadedFile of uploadedFiles();
            track uploadedFile;
            let i = $index;
            let last = $last
          ) {
            <div
              class="flex align-items-center gap-3 p-3 hover:surface-50 border-round transition-all transition-duration-200"
              [ngClass]="{ 'border-bottom-1 border-200': !last }"
              [attr.data-testid]="'uploaded-file-item-' + i"
            >
              <!-- File Preview/Icon -->
              <div class="flex-shrink-0">
                @if (isImageFile(uploadedFile.file) && uploadedFile.preview) {
                  <div
                    class="border-round overflow-hidden border-1 border-200 shadow-1"
                    style="width: 56px; height: 56px"
                  >
                    <img
                      [src]="uploadedFile.preview"
                      [alt]="uploadedFile.file.name"
                      class="w-full h-full object-cover"
                      [attr.data-testid]="'file-preview-' + i"
                    />
                  </div>
                }
                @if (!isImageFile(uploadedFile.file) || !uploadedFile.preview) {
                  <div
                    class="flex align-items-center justify-content-center border-round surface-100 border-1 border-200 shadow-1"
                    style="width: 56px; height: 56px"
                  >
                    <i
                      class="fa {{ getFileIcon(uploadedFile.file) }} text-3xl"
                      [attr.data-testid]="'file-icon-' + i"
                    ></i>
                  </div>
                }
              </div>

              <!-- File Info -->
              <div class="flex-1 overflow-hidden">
                <div
                  class="font-semibold text-sm text-900 white-space-nowrap overflow-hidden text-overflow-ellipsis mb-1"
                  [pTooltip]="uploadedFile.file.name"
                  tooltipPosition="top"
                  [attr.data-testid]="'file-name-' + i"
                >
                  {{ uploadedFile.file.name }}
                </div>
                <div class="flex align-items-center gap-2">
                  <span class="text-xs text-600" [attr.data-testid]="'file-size-' + i">
                    <i class="fa fa-database mr-1"></i>
                    {{ getFileSize(uploadedFile.file) }}
                  </span>
                  <span class="text-xs text-500">•</span>
                  <span class="text-xs text-green-600 font-semibold">
                    <i class="fa fa-check mr-1"></i>
                    Uploaded
                  </span>
                </div>
              </div>

              <!-- Remove Button -->
              <div class="flex-shrink-0">
                <p-button
                  icon="fa fa-trash"
                  [text]="true"
                  [rounded]="true"
                  size="small"
                  severity="danger"
                  (onClick)="onRemoveFile(i)"
                  [pTooltip]="'FREEZE.FREEZE_SIDEBAR.REMOVE_FILE' | translate"
                  tooltipPosition="left"
                  styleClass="hover:bg-red-50"
                  [attr.aria-label]="'remove-file-button-' + i"
                  [attr.data-testid]="'remove-file-button-' + i"
                />
              </div>
            </div>
          }
        </div>
      </div>

      <!-- Add More Files Button -->
      <div class="mt-3 text-center" data-testid="add-more-files-section">
        <div class="relative inline-block" style="isolation: isolate">
          <p-button
            [label]="'FREEZE.FREEZE_SIDEBAR.ADD_MORE_FILES' | translate"
            icon="fa fa-plus"
            styleClass="p-button-outlined"
            severity="secondary"
            aria-label="add-more-files-button"
            data-testid="add-more-files-button"
          />
          <ngx-file-drop
            class="opacity-0 absolute w-full h-full top-0 left-0 cursor-pointer"
            style="z-index: 2"
            [accept]="acceptedFileTypes.join(',')"
            (onFileDrop)="onFileDrop($event)"
            aria-label="add-more-files-drop-zone"
            data-testid="add-more-files-drop-zone"
          >
            <ng-template ngx-file-drop-content-tmp let-openFileSelector="openFileSelector">
              <div
                (click)="openFileSelector()"
                class="w-full h-full absolute left-0 top-0"
              ></div>
            </ng-template>
          </ngx-file-drop>
        </div>
      </div>
    </div>
  }
</div>
